import { Group, Stack } from '@mantine/core';
import { useCallback } from 'react';
import type { Measure } from '../../../../api/work-periods-client/work-periods-client.type';
import { useTranslation } from 'react-i18next';
import { ResetFiltersButton } from '../../../../components/filters/reset-filters-button';
import { useProcessAnalysisStore } from '../../../../store/process-analysis-store/process-analysis-store';
import {
  resetKeyMeasuresOverTimeFilters,
  setSelectedMeasureOverTime,
  setSelectedMeasureOverTimeTrend,
} from '../../../../store/process-analysis-store/process-analysis-store.actions';
import {
  useSelectedMeasureOverTime,
  useSelectedMeasureOverTimeTrend,
} from '../../../../store/process-analysis-store/process-analysis-store.hooks';
import { Checkbox } from '../../../../ui-library/checkbox/checkbox';
import { Radio } from '../../../../ui-library/radio/radio';
import { Text } from '../../../../ui-library/typography/typography';
import { formatMeasureName } from '../../process-analysis.helpers';
import { useAutoSelectFilters } from './key-measures-over-time.hooks';

export const KeyMeasuresOverTime = () => {
  const i18n = useTranslation();
  const selectedMeasureOverTime = useSelectedMeasureOverTime();
  const selectedMeasureOverTimeTrend = useSelectedMeasureOverTimeTrend();
  const availableMeasures = useProcessAnalysisStore((state) => state.availableMeasures);

  useAutoSelectFilters();

  const handleMeasureChange = useCallback((measureName: Measure) => {
    setSelectedMeasureOverTime(measureName);
    setSelectedMeasureOverTimeTrend(false);
  }, []);

  const handleTrendChange = useCallback((checked: boolean) => {
    setSelectedMeasureOverTimeTrend(checked);
  }, []);

  if (availableMeasures.length === 0) {
    return null;
  }

  return (
    <Stack pb={40}>
      <Group justify="space-between">
        <Text size="small" weight="bold">
          {i18n.t('process_analysis.filters.key_measures')}
        </Text>
        <Text size="small" weight="bold">
          {i18n.t('process_analysis.filters.trends')}
        </Text>
      </Group>
      {availableMeasures.map((measure) => {
        const isSelected = selectedMeasureOverTime === measure.measure_name;

        return (
          <Group key={measure.measure_name} justify="space-between">
            <Group gap="sm" wrap="nowrap" style={{ flex: 1 }}>
              <Radio
                value={measure.measure_name}
                checked={isSelected}
                onChange={() => handleMeasureChange(measure.measure_name)}
                label={formatMeasureName(measure.measure_name, i18n.t)}
                size="sm"
              />
            </Group>
            {isSelected && (
              <Checkbox
                checked={selectedMeasureOverTimeTrend}
                onChange={(event) => handleTrendChange(event.currentTarget.checked)}
                size="sm"
              />
            )}
          </Group>
        );
      })}
      <ResetFiltersButton
        onClick={() => {
          resetKeyMeasuresOverTimeFilters(availableMeasures[0].measure_name);
        }}
      />
    </Stack>
  );
};
